import { countTopicOverlap, normalizeTopicKey, parseTopics, topicKeys } from '../topics/topic.utils';

/**
 * Heuristic score for "Who to follow" recommendations (P3.D2 / Suggestion Engine M1).
 *
 * Weights are intentionally simple and stable — ranking v2 (fuzzy topics,
 * diversity, dismiss feedback, ML) is deferred to backlog.
 *
 * Formula:
 *   topicOverlap × 3
 * + min(followerCount / 100, 5)
 * + min(publishedPostsLast30d, 5)
 * + 2 if founding creator badge
 * + 1 if active creator has a published Circle (soft monetization awareness)
 */
export type SuggestedFollowScoreInput = {
  topicOverlap: number;
  followerCount: number;
  publishedPostsLast30d: number;
  isFoundingCreator?: boolean;
  /** Soft boost — does not pay-to-rank; only marks public offering presence. */
  hasPublishedCircle?: boolean;
};

export function computeSuggestedFollowScore(input: SuggestedFollowScoreInput): number {
  return (
    input.topicOverlap * 3 +
    Math.min(input.followerCount / 100, 5) +
    Math.min(input.publishedPostsLast30d, 5) +
    (input.isFoundingCreator ? 2 : 0) +
    (input.hasPublishedCircle ? 1 : 0)
  );
}

/** Returns overlapping topic labels from the candidate profile (preserves candidate casing). */
export function listMatchedTopics(
  viewerTopics: Set<string> | string[] | null | undefined,
  candidateTopics: string[] | null | undefined,
): string[] {
  const viewerKeys = viewerTopics instanceof Set ? viewerTopics : topicKeys(viewerTopics ?? []);
  const parsed = parseTopics(candidateTopics);
  const matched: string[] = [];
  const seen = new Set<string>();
  for (const topic of parsed) {
    const key = normalizeTopicKey(topic);
    if (viewerKeys.has(key) && !seen.has(key)) {
      seen.add(key);
      matched.push(topic);
    }
  }
  return matched;
}

export function countSuggestedFollowTopicOverlap(
  viewerTopics: Set<string> | string[] | null | undefined,
  candidateTopics: string[] | null | undefined,
): number {
  return countTopicOverlap(viewerTopics, candidateTopics);
}
